import React, { createContext, useContext, useState, useEffect } from 'react';
import Fuse from 'fuse.js';
import { useCampaign } from './CampaignContext';
import { useAuth } from './AuthContext';

const SearchContext = createContext();

export function useSearch() {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
}

export function SearchProvider({ children }) {
  const { canEdit, isModuleEnabled } = useCampaign();
  const { authEpoch } = useAuth();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [allData, setAllData] = useState({
    quests: [],
    locations: [],
    npcs: [],
    items: [],
    sessions: [],
    character: null,
    secrets: []
  });
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch all data when search is opened. authEpoch is also a dependency —
  // see the identical comment in components/tabs/ItemsTab.jsx — so a session
  // that expired while the search modal happened to already be open recovers
  // too, not just the "opened after reauth" case already covered by
  // isSearchOpen above.
  useEffect(() => {
    if (isSearchOpen) {
      // Always refetch when search is opened to ensure fresh data
      fetchAllData();
    }
  }, [isSearchOpen, authEpoch]);

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      const want = (key) => isModuleEnabled(key);
      // A non-OK response (e.g. a 401) still has a JSON body (`{error:
      // "..."}`), not an array. filterByVisibility below only guards the
      // non-canEdit path with Array.isArray — for canEdit it returns
      // whatever it was handed as-is — so an unguarded r.json() here could
      // still leak a stray non-array into allData. Short-circuit at the
      // fetch itself instead, same as the other data-loading call sites.
      const [quests, locations, npcs, items, sessions, character, secrets] = await Promise.all([
        want('quests')    ? fetch('/api/quests').then(r => (r.ok ? r.json() : []))     : Promise.resolve([]),
        want('locations') ? fetch('/api/locations').then(r => (r.ok ? r.json() : []))  : Promise.resolve([]),
        want('npcs')      ? fetch('/api/npcs').then(r => (r.ok ? r.json() : []))       : Promise.resolve([]),
        want('items')     ? fetch('/api/items').then(r => (r.ok ? r.json() : []))      : Promise.resolve([]),
        want('sessions')  ? fetch('/api/sessions').then(r => (r.ok ? r.json() : []))   : Promise.resolve([]),
        want('character') ? fetch('/api/character').then(r => (r.ok ? r.json() : null)) : Promise.resolve(null),
        canEdit           ? fetch('/api/secrets').then(r => (r.ok ? r.json() : []))    : Promise.resolve([])
      ]);

      // Filter by visibility for non-admin users (fog of war)
      const filterByVisibility = (items) => {
        if (canEdit) return items;
        return Array.isArray(items) ? items.filter(item => item.is_visible === 1) : [];
      };

      const playerSecrets = canEdit
        ? (Array.isArray(secrets) ? secrets : [])
        : (character?.secrets || []);

      setAllData({
        quests: filterByVisibility(quests),
        locations: filterByVisibility(locations),
        npcs: filterByVisibility(npcs),
        items: filterByVisibility(items),
        sessions: filterByVisibility(sessions),
        character,
        // For players, include their assigned secrets from character data
        secrets: playerSecrets
      });
    } catch (error) {
      console.error('Error fetching search data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Perform search when query changes
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    performSearch(searchQuery);
  }, [searchQuery, allData]);

  const performSearch = (query) => {
    const results = [];

    // Configure Fuse.js for each content type
    const fuseOptions = {
      threshold: 0.4,
      includeScore: true,
      minMatchCharLength: 2
    };

    // Search Quests
    if (allData.quests.length > 0) {
      const questFuse = new Fuse(allData.quests, {
        ...fuseOptions,
        keys: [
          { name: 'title', weight: 0.7 },
          { name: 'description', weight: 0.3 }
        ]
      });
      const questResults = questFuse.search(query);
      questResults.forEach(result => {
        results.push({
          type: 'quest',
          icon: '⚔️',
          data: result.item,
          score: result.score
        });
      });
    }

    // Search Locations
    if (allData.locations.length > 0) {
      const locationFuse = new Fuse(allData.locations, {
        ...fuseOptions,
        keys: [
          { name: 'name', weight: 0.7 },
          { name: 'description', weight: 0.3 },
          { name: 'location_type', weight: 0.2 }
        ]
      });
      const locationResults = locationFuse.search(query);
      locationResults.forEach(result => {
        results.push({
          type: 'location',
          icon: '🗺️',
          data: result.item,
          score: result.score
        });
      });
    }

    // Search NPCs
    if (allData.npcs.length > 0) {
      const npcFuse = new Fuse(allData.npcs, {
        ...fuseOptions,
        keys: [
          { name: 'name', weight: 0.7 },
          { name: 'title', weight: 0.5 },
          { name: 'description', weight: 0.3 },
          { name: 'tags', weight: 0.2 }
        ]
      });
      const npcResults = npcFuse.search(query);
      npcResults.forEach(result => {
        results.push({
          type: 'npc',
          icon: result.item.entity_type === 'faction' ? '🏛️' : '👤',
          data: result.item,
          score: result.score
        });
      });
    }

    // Search Items
    if (allData.items.length > 0) {
      const itemFuse = new Fuse(allData.items, {
        ...fuseOptions,
        keys: [
          { name: 'name', weight: 0.7 },
          { name: 'description', weight: 0.3 },
          { name: 'item_type', weight: 0.2 }
        ]
      });
      const itemResults = itemFuse.search(query);
      itemResults.forEach(result => {
        results.push({
          type: 'item',
          icon: result.item.icon || '⚗️',
          data: result.item,
          score: result.score
        });
      });
    }

    // Search Sessions
    if (allData.sessions.length > 0) {
      const sessionFuse = new Fuse(allData.sessions, {
        ...fuseOptions,
        keys: [
          { name: 'title', weight: 0.7 },
          { name: 'summary', weight: 0.3 }
        ]
      });
      const sessionResults = sessionFuse.search(query);
      sessionResults.forEach(result => {
        results.push({
          type: 'session',
          icon: '📜',
          data: result.item,
          score: result.score
        });
      });
    }

    // Search Character (if player)
    if (allData.character && !canEdit) {
      const characterData = [{
        name: allData.character.character_name,
        description: [
          allData.character.appearance,
          allData.character.personality,
          allData.character.backstory
        ].filter(Boolean).join(' ')
      }];

      const characterFuse = new Fuse(characterData, {
        ...fuseOptions,
        keys: [
          { name: 'name', weight: 0.7 },
          { name: 'description', weight: 0.3 }
        ]
      });
      const characterResults = characterFuse.search(query);
      if (characterResults.length > 0) {
        results.push({
          type: 'character',
          icon: '🎭',
          data: allData.character,
          score: characterResults[0].score
        });
      }
    }

    // Search Secrets (admin sees all, players see only assigned secrets)
    if (allData.secrets.length > 0) {
      const secretFuse = new Fuse(allData.secrets, {
        ...fuseOptions,
        keys: [
          { name: 'name', weight: 0.7 },
          { name: 'description', weight: 0.3 },
          { name: 'benefits', weight: 0.2 },
          { name: 'drawbacks', weight: 0.2 }
        ]
      });
      const secretResults = secretFuse.search(query);

      secretResults.forEach(result => {
        results.push({
          type: 'secret',
          icon: '🔮',
          data: result.item,
          score: result.score
        });
      });
    }

    // Sort by score (lower is better with Fuse.js)
    results.sort((a, b) => a.score - b.score);
    setSearchResults(results);
  };

  const openSearch = () => {
    setIsSearchOpen(true);
  };

  const closeSearch = () => {
    setIsSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const value = {
    isSearchOpen,
    searchQuery,
    searchResults,
    isLoading,
    openSearch,
    closeSearch,
    setSearchQuery,
    refreshData: fetchAllData
  };

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
}
